import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X, MapPin } from "lucide-react";
import toast from "react-hot-toast";
import { createRegisteredUser, getRegisteredUserByUsername, updateRegisteredUser } from "@/services/users";
import { getMasterDropdownService, getCitiesBasedOnState } from "@/services/masters";
import { FormField } from "@/components/ui/FormField";
import Dropdown from "@/components/ui/Dropdown";
import FullScreenLocationPicker from "@/components/common/LocationSelector";

const userSchema = z.object({
  Username: z.string().min(1, "Username is required"),
  Email: z.string().min(1, "Email is required").email("Invalid email address"),
  Password: z.string().optional(),
  Conform_Password: z.string().optional(),
  First_Name: z.string().min(1, "First name is required"),
  Last_Name: z.string().min(1, "Last name is required"),
  Phone: z.string().min(10, "Phone must be 10 digits").max(10, "Phone must be 10 digits"),
  Alternative_Phone: z.string().optional().refine((val) => !val || val.length === 10, {
    message: "Phone must be 10 digits",
  }),
  Address: z.string().min(1, "Please select a location on the map"),
  Latitude: z.string().min(1, "Please select a location on the map"),
  Longitude: z.string().min(1, "Please select a location on the map"),
  State: z.string().min(1, "State is required"),
  City: z.string().min(1, "City is required"),
}).superRefine((data, ctx) => {
  if (data.Password && data.Password.length > 0) {
    if (data.Password.length < 6) {
      ctx.addIssue({ code: "custom", message: "Password must be at least 6 characters", path: ["Password"] });
    }
    if (!data.Conform_Password) {
      ctx.addIssue({ code: "custom", message: "Confirm password is required", path: ["Conform_Password"] });
    }
    if (data.Password !== data.Conform_Password) {
      ctx.addIssue({ code: "custom", message: "Passwords do not match", path: ["Conform_Password"] });
    }
  }
});

type UserFormData = z.infer<typeof userSchema>;

interface UserModalProps {
  user: any | null;
  onClose: () => void;
  onSuccess: (createdUsername?: string) => void;
}

export default function UserModal({ user, onClose, onSuccess }: UserModalProps) {
  const isEditing = !!user;
  const [states, setStates] = useState<{ id: number; label: string; value: string }[]>([]);
  const [cities, setCities] = useState<{ id: number; label: string; value: string }[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const pendingCityRef = useRef<string>("");
  const username = user?.Username || user?.user?.username || "";
  const rawStatesRef = useRef<any[]>([]);

  const { control, handleSubmit, reset, watch, setValue, setError, clearErrors, formState: { isSubmitting, errors } } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      Username: "", Email: "", Password: "", Conform_Password: "",
      First_Name: "", Last_Name: "", Phone: "", Alternative_Phone: "",
      Address: "", Latitude: "", Longitude: "", State: "", City: "",
    }
  });

  const watchedState = watch("State");
  const lat = watch("Latitude");
  const lng = watch("Longitude");
  const addr = watch("Address");


  useEffect(() => {
    if (!watchedState) {
      setCities([]);
      setValue("City", "");
      pendingCityRef.current = "";
      return;
    }

    clearErrors("State");

    const loadCities = async () => {
      setLoadingCities(true);

      try {
        const response = await getCitiesBasedOnState(watchedState);

        if (response.status === 200 && response.data) {
          const items: any[] = (response.data as any)?.Cities ?? [];

          const cityList = items.map((c: any, idx: number) => ({
            id: typeof c === "object" ? (c.id ?? idx) : idx,
            label: typeof c === "object" ? c.City_Name : c,
            value:
              typeof c === "object"
                ? c.unique_code || c.City_Name
                : c,
          }));

          setCities(cityList);

          if (pendingCityRef.current) {
            const matchedCity = cityList.find(
              (c) =>
                c.label === pendingCityRef.current ||
                c.value === pendingCityRef.current
            );

            if (matchedCity) {
              setValue("City", matchedCity.value);
            }

            pendingCityRef.current = "";
          }
        }
      } catch (error) {
        console.error("Failed to load cities:", error);
      } finally {
        setLoadingCities(false);
      }
    };

    loadCities();
  }, [watchedState]);

  async function getUserDetails(rawStates: any[]) {
    try {
      const { data } = await getRegisteredUserByUsername(username);
      const userData = data[0];
      const stateCode = rawStates.find((s: any) => s.Name === userData?.State)?.unique_code
        || rawStates.find((s: any) => s.unique_code === userData?.State)?.unique_code
        || "";
      pendingCityRef.current = userData?.City || "";
      reset({
        Username: userData?.Username || "",
        Email: userData?.Email || "",
        Password: "", Conform_Password: "",
        First_Name: userData?.First_Name || "",
        Last_Name: userData?.Last_Name || "",
        Phone: userData?.Phone || "",
        Alternative_Phone: userData?.Alternative_Phone || "",
        Address: userData?.Address || "",
        Latitude: userData?.Latitude?.toString() || "",
        Longitude: userData?.Longitude?.toString() || "",
        State: stateCode,
        City: userData?.City || "",
      });
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  }

  useEffect(() => {
    const init = async () => {
      setLoadingStates(true);
      try {
        const response = await getMasterDropdownService();
        if (response.status === 200 && response.data) {
          const rawStates: any[] = (response.data as any).States || [];

          rawStatesRef.current = rawStates;

          setStates(
            rawStates.map((s: any, idx: number) => ({
              id: s.id ?? idx,
              label: s.Name,
              value: s.unique_code || s.Name,
            }))
          );
          if (user) await getUserDetails(rawStates);
        }
      } catch (error) {
        console.error("Failed to load states:", error);
      } finally {
        setLoadingStates(false);
      }
    };
    init();
  }, [user, reset]);

  const resolveStateName = (codeOrName: string) =>
    rawStatesRef.current.find(
      (s: any) =>
        s.unique_code === codeOrName || s.Name === codeOrName
    )?.Name || codeOrName;

  const resolveCityName = (
    codeOrName: string,
    cityList: typeof cities
  ) =>
    cityList.find(
      (c) => c.value === codeOrName || c.label === codeOrName
    )?.label || codeOrName;

  const handleLocationChange = (loc: { latitude: string; longitude: string; address: string }) => {
    setValue("Latitude", loc.latitude, { shouldValidate: true });
    setValue("Longitude", loc.longitude, { shouldValidate: true });
    setValue("Address", loc.address, { shouldValidate: true });
    clearErrors(["Latitude", "Longitude", "Address"]);
  };

  const onSubmit = async (data: UserFormData) => {
    clearErrors();
    try {
      const stateName = states.find(s => s.value === data.State)?.label || data.State || "";
      const cityName = cities.find(c => c.value === data.City)?.label || data.City || "";

      let response;
      if (isEditing) {
        const updateData: any = {
          First_Name: data.First_Name,
          Last_Name: data.Last_Name,
          Phone: data.Phone,
          Alternative_Phone: data.Alternative_Phone,
          Address: data.Address,
          Latitude: data.Latitude,
          Longitude: data.Longitude,
        };
        if (data.State) { updateData.State = stateName; updateData.State_Code = data.State; }
        if (data.City) { updateData.City = cityName; updateData.City_Code = data.City; }
        response = await updateRegisteredUser(user.id, updateData);
      } else {
        const createData: any = {
          Username: data.Username,
          Email: data.Email,
          Password: data.Password!,
          Conform_Password: data.Conform_Password!,
          Last_Name: data.Last_Name,
          Phone: data.Phone,
          ...(data.Alternative_Phone ? { Alternative_Phone: data.Alternative_Phone } : {}),
          Address: data.Address,
          Latitude: data.Latitude,
          Longitude: data.Longitude,
        };
        createData.First_Name = data.First_Name;
        if (data.State) { createData.State = stateName; createData.State_Code = data.State; }
        if (data.City) { createData.City = cityName; createData.City_Code = data.City; }
        response = await createRegisteredUser(createData);
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(isEditing ? "User updated successfully" : "User created successfully");
        if (!isEditing) {
          onSuccess(data.Username);
          return;
        }
        onSuccess();
      } else if (response.errors && typeof response.errors === "object") {
        Object.entries(response.errors).forEach(([key, value]) => {
          const errorText = Array.isArray(value) ? value[0] : String(value);
          if (key === "error" || key === "non_field_errors" || key === "detail") {
            toast.error(errorText);
          } else {
            setError(key as keyof UserFormData, { type: "server", message: errorText });
          }
        });
      } else {
        toast.error("Failed to save user");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Error saving user");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-20 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 rounded-t-xl" style={{ background: 'linear-gradient(135deg, #2d3440 0%, #535c6b 100%)' }}>
          <h3 className="text-2xl font-bold text-white">{isEditing ? "Edit User" : "Add New User"}</h3>
          <button onClick={onClose} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors" disabled={isSubmitting}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {!isEditing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormField control={control} name="Username" label="Username" placeholder="Enter username" required error={errors.Username?.message} />
                </div>
                <div className="md:col-span-2">
                  <FormField control={control} name="Email" label="Email" type="email" placeholder="Enter email" required error={errors.Email?.message} />
                </div>
                <FormField control={control} name="Password" label="Password" type="password" placeholder="Enter password" required error={errors.Password?.message} />
                <FormField control={control} name="Conform_Password" label="Confirm Password" type="password" placeholder="Confirm password" required error={errors.Conform_Password?.message} />
                <FormField control={control} name="First_Name" label="First Name" placeholder="Enter first name" required error={errors.First_Name?.message} />
                <FormField control={control} name="Last_Name" label="Last Name" placeholder="Enter last name" required error={errors.Last_Name?.message} />
                <FormField control={control} name="Phone" label="Phone" type="tel" placeholder="10-digit phone" maxLength={10} required error={errors.Phone?.message} />
                <FormField control={control} name="Alternative_Phone" label="Alternative Phone" type="tel" placeholder="10-digit phone" maxLength={10} error={errors.Alternative_Phone?.message} />
                <Dropdown name="State" control={control} label="State" options={states} placeholder="Select State" isLoading={loadingStates} error={errors.State?.message} onClear={() => clearErrors("State")} required />
                <Dropdown name="City" control={control} label="District" options={cities} placeholder={watchedState ? "Select District" : "Select state first"} disabled={!watchedState} isLoading={loadingCities} error={errors.City?.message} onClear={() => clearErrors("City")} required />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormField control={control} name="Username" label="Username" disabled />
                </div>
                <div className="md:col-span-2">
                  <FormField control={control} name="Email" label="Email" type="email" disabled />
                </div>
                <FormField control={control} name="First_Name" label="First Name" placeholder="Enter first name" required error={errors.First_Name?.message} />
                <FormField control={control} name="Last_Name" label="Last Name" placeholder="Enter last name" required error={errors.Last_Name?.message} />
                <FormField control={control} name="Phone" label="Phone" type="tel" placeholder="10-digit phone" maxLength={10} required error={errors.Phone?.message} />
                <FormField control={control} name="Alternative_Phone" label="Alternative Phone" type="tel" placeholder="10-digit phone" maxLength={10} error={errors.Alternative_Phone?.message} />
                <Dropdown name="State" control={control} label="State" options={states} placeholder="Select State" isLoading={loadingStates} error={errors.State?.message} onClear={() => clearErrors("State")} required />
                <Dropdown name="City" control={control} label="District" options={cities} placeholder={watchedState ? "Select District" : "Select state first"} disabled={!watchedState} isLoading={loadingCities} error={errors.City?.message} onClear={() => clearErrors("City")} required />
                <div className="md:col-span-2">
                  <FormField control={control} name="Address" label="Address" placeholder="Enter address" isTextarea rows={2} error={errors.Address?.message} />
                </div>
              </div>
            )}

            {/* Map Location Picker */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </p>
              {addr ? (
                <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 mb-2 flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>{addr}</span>
                </div>
              ) : (errors.Address || errors.Latitude) && (
                <p className="text-xs text-red-500 mb-2">{errors.Address?.message || errors.Latitude?.message}</p>
              )}
              <button
                type="button"
                onClick={() => setMapOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-orange-400 text-orange-600 hover:bg-orange-50 transition-colors text-sm font-semibold"
              >
                <MapPin className="w-4 h-4" />
                {lat && lng ? "Change Location" : "Select Location"}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-gray-200 bg-white rounded-b-xl">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold py-2.5 rounded-lg transition-all disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 bg-mlb-gold hover:bg-mlb-gold/80 disabled:opacity-50 text-brand-700 font-semibold py-2.5 rounded-lg transition-all">
              {isSubmitting ? "Saving..." : isEditing ? "Update User" : "Create User"}
            </button>
          </div>
        </form>
      </div>

      <FullScreenLocationPicker
        isOpen={mapOpen}
        onClose={() => setMapOpen(false)}
        value={lat && lng ? { latitude: lat, longitude: lng } : undefined}
        onConfirm={(loc) => { handleLocationChange(loc); setMapOpen(false); }}
        parentUsername={user?.Username}
        selectedState={resolveStateName(watchedState)}
        selectedCity={resolveCityName(watch("City"), cities)} />
    </div>
  );
}
